import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../services/api';
import { decrypt } from '../utils/security';

export default function BooksPage() {
  const [books,      setBooks]      = useState([]);
  const [pagination, setPag]        = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [filter,     setFilter]     = useState('all'); // all | enabled | disabled
  const [page,       setPage]       = useState(1);
  const [selected,   setSelected]   = useState(new Set());
  const [toast,      setToast]      = useState(null);
  const [toggling,   setToggling]   = useState(new Set()); // ids currently toggling

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const loadBooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:20 };
      if (search) params.search = search;
      if (filter !== 'all') params.status = filter;
      const res = await adminAPI.getBooks(params);
      setBooks(res.data || []);
      setPag(res.pagination);
    } catch(e) { showToast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [page, search, filter]);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  const handleToggle = async (book) => {
    const newEnabled = book.enabled === false ? true : false; // toggle
    setToggling(prev => new Set(prev).add(book._id));
    try {
      await adminAPI.toggleBookStatus(book._id, newEnabled);
      setBooks(prev => prev.map(b => b._id === book._id ? { ...b, enabled: newEnabled } : b));
      showToast(`"${decrypt(book.book_name || book.book_title)}" ${newEnabled ? 'enabled ✓' : 'disabled'}`);
    } catch(e) { showToast(e.message, 'error'); }
    finally { setToggling(prev => { const s = new Set(prev); s.delete(book._id); return s; }); }
  };

  const handleFlagToggle = async (book, field) => {
    const newVal = !book[field];
    const key = `${book._id}-${field}`;
    setToggling(prev => new Set(prev).add(key));
    try {
      await adminAPI.updateBookField(book._id, field, newVal);
      setBooks(prev => prev.map(b => b._id === book._id ? { ...b, [field]: newVal } : b));
      showToast(`${field} updated for "${decrypt(book.book_name || book.book_title)}" ✓`);
    } catch(e) { showToast(e.message, 'error'); }
    finally { setToggling(prev => { const s = new Set(prev); s.delete(key); return s; }); }
  };

  const handleBulk = async (enable) => {
    if (selected.size === 0) return;
    try {
      await adminAPI.bulkToggle(Array.from(selected), enable);
      setBooks(prev => prev.map(b => selected.has(b._id) ? { ...b, enabled: enable } : b));
      setSelected(new Set());
      showToast(`${selected.size} books ${enable ? 'enabled' : 'disabled'} ✓`);
    } catch(e) { showToast(e.message, 'error'); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  const selectAll = () => {
    if (selected.size === books.length) setSelected(new Set());
    else setSelected(new Set(books.map(b => b._id)));
  };

  let searchTimeout;
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => { setPage(1); }, 400);
  };

  return (
    <div style={{ padding:32, fontFamily:'Nunito,sans-serif' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontFamily:'Playfair Display,serif', fontSize:26, fontWeight:700, color:'#2C2C35' }}>
            Book <em style={{ color:'#C97B77', fontStyle:'italic' }}>Management</em>
          </div>
          <div style={{ fontSize:13, color:'#888899', marginTop:2 }}>
            Enable or disable books — disabled books are hidden from all users
          </div>
        </div>
        {pagination && (
          <div style={{ fontSize:13, color:'#888899' }}>
            {pagination.total} total books
          </div>
        )}
      </div>

      {/* Search + Filter bar */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{ flex:1, minWidth:260, position:'relative' }}>
          <span style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', fontSize:15, color:'#AAAABC' }}>🔍</span>
          <input value={search} onChange={e => handleSearch(e.target.value)}
            placeholder="Search books by name…"
            style={{ width:'100%', padding:'10px 14px 10px 38px', borderRadius:10, border:'2px solid #E8E4DF', fontSize:13,
              fontFamily:'Nunito,sans-serif', color:'#2C2C35', outline:'none', boxSizing:'border-box' }}/>
        </div>
        {/* Filter chips */}
        <div style={{ display:'flex', gap:6 }}>
          {[['all','All'],['enabled','✅ Enabled'],['disabled','🚫 Disabled']].map(([val, lbl]) => (
            <button key={val} onClick={() => { setFilter(val); setPage(1); }}
              style={{ padding:'8px 14px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'Nunito,sans-serif',
                background: filter===val ? '#C97B77' : 'white', color: filter===val ? 'white' : '#555567',
                border: filter===val ? 'none' : '2px solid #E8E4DF' }}>
              {lbl}
            </button>
          ))}
        </div>
        {/* Bulk actions */}
        {selected.size > 0 && (
          <div style={{ display:'flex', gap:8, alignItems:'center', padding:'8px 14px', background:'#EEF2F8', borderRadius:10, border:'1px solid #A8B8D0' }}>
            <span style={{ fontSize:12, fontWeight:700, color:'#6B7FA0' }}>{selected.size} selected</span>
            <button onClick={() => handleBulk(true)}
              style={{ padding:'5px 12px', background:'#7A9E8E', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer' }}>
              ✅ Enable All
            </button>
            <button onClick={() => handleBulk(false)}
              style={{ padding:'5px 12px', background:'#C97B77', color:'white', border:'none', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer' }}>
              🚫 Disable All
            </button>
            <button onClick={() => setSelected(new Set())}
              style={{ padding:'5px 8px', background:'transparent', color:'#888899', border:'none', fontSize:16, cursor:'pointer' }}>✕</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ background:'white', borderRadius:14, border:'1px solid #E8E4DF', overflow:'hidden' }}>
        {/* Table header */}
        <div style={{ display:'grid', gridTemplateColumns:'40px 1fr 140px 100px 80px 80px 100px 80px', gap:0,
          padding:'10px 16px', background:'#F5F2EE', borderBottom:'1px solid #E8E4DF', fontSize:10,
          fontWeight:700, color:'#888899', textTransform:'uppercase', letterSpacing:1.5 }}>
          <div>
            <input type="checkbox" checked={selected.size === books.length && books.length > 0}
              onChange={selectAll} style={{ cursor:'pointer' }}/>
          </div>
          <div>Book Name</div>
          <div>Category</div>
          <div>Author</div>
          <div style={{ textAlign:'center' }}>AIR Online</div>
          <div style={{ textAlign:'center' }}>Lawspice</div>
          <div>Status</div>
          <div>Action</div>
        </div>

        {loading ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:40, gap:12 }}>
            <div style={{ width:24, height:24, border:'2px solid #E8E4DF', borderTopColor:'#C97B77', borderRadius:'50%', animation:'spin .8s linear infinite' }}/>
            <span style={{ fontSize:13, color:'#888899' }}>Loading books…</span>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </div>
        ) : books.length === 0 ? (
          <div style={{ textAlign:'center', padding:40, color:'#888899', fontSize:13 }}>
            No books found{search ? ` for "${search}"` : ''}
          </div>
        ) : books.map((book, i) => {
          const isEnabled = book.enabled !== false;
          const isToggling = toggling.has(book._id);
          const title = decrypt(book.book_name || book.book_title || '—');
          const author = book.authors?.[0]?.name || '—';
          const cat = book.category?.name || '—';
          return (
            <div key={book._id}
              style={{ display:'grid', gridTemplateColumns:'40px 1fr 140px 100px 80px 80px 100px 80px', gap:0,
                padding:'12px 16px', borderBottom:'1px solid #F0ECE8', alignItems:'center',
                background: selected.has(book._id) ? '#FDF5F4' : i%2===0 ? 'white' : '#FAFAF8',
                transition:'background .15s' }}
              onMouseEnter={e => { if (!selected.has(book._id)) e.currentTarget.style.background='#FDF5F4'; }}
              onMouseLeave={e => { if (!selected.has(book._id)) e.currentTarget.style.background= i%2===0?'white':'#FAFAF8'; }}>

              <div>
                <input type="checkbox" checked={selected.has(book._id)} onChange={() => toggleSelect(book._id)} style={{ cursor:'pointer' }}/>
              </div>

              <div style={{ paddingRight:12 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#2C2C35', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:320 }} title={title}>
                  {title}
                </div>
                <div style={{ fontSize:10, color:'#888899', marginTop:2 }}>ID: {book._id}</div>
              </div>

              <div style={{ fontSize:11, color:'#6B7FA0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cat}</div>

              <div style={{ fontSize:11, color:'#555567', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{author.length > 12 ? author.substring(0,12)+'…' : author}</div>

              <div style={{ textAlign:'center' }}>
                <input type="checkbox" checked={!!book.aironline} disabled={toggling.has(`${book._id}-aironline`)}
                  onChange={() => handleFlagToggle(book, 'aironline')} style={{ cursor:'pointer' }}/>
              </div>

              <div style={{ textAlign:'center' }}>
                <input type="checkbox" checked={!!book.lawspice} disabled={toggling.has(`${book._id}-lawspice`)}
                  onChange={() => handleFlagToggle(book, 'lawspice')} style={{ cursor:'pointer' }}/>
              </div>

              <div>
                <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'4px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                  background: isEnabled ? '#EBF4F0' : '#FBF4E8',
                  color:      isEnabled ? '#7A9E8E' : '#C4975A' }}>
                  <span>{isEnabled ? '●' : '○'}</span>
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>

              <div>
                <button onClick={() => handleToggle(book)} disabled={isToggling}
                  style={{ padding:'6px 12px', borderRadius:7, fontSize:11, fontWeight:700, cursor: isToggling ? 'not-allowed' : 'pointer', border:'none',
                    background: isEnabled ? '#FAE8E6' : '#EBF4F0',
                    color:      isEnabled ? '#C97B77' : '#7A9E8E',
                    opacity: isToggling ? .6 : 1, fontFamily:'Nunito,sans-serif' }}>
                  {isToggling ? '…' : isEnabled ? 'Disable' : 'Enable'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:20 }}>
          <button disabled={!pagination.has_prev} onClick={() => setPage(p=>p-1)}
            style={{ padding:'8px 16px', borderRadius:8, border:'2px solid #E8E4DF', background:'white', color:'#555567', fontSize:12, fontWeight:700, cursor: pagination.has_prev ? 'pointer' : 'not-allowed', opacity: pagination.has_prev ? 1 : .4 }}>
            ‹ Prev
          </button>
          <span style={{ fontSize:12, color:'#888899', padding:'0 8px' }}>Page {pagination.page} of {pagination.total_pages}</span>
          <button disabled={!pagination.has_next} onClick={() => setPage(p=>p+1)}
            style={{ padding:'8px 16px', borderRadius:8, border:'2px solid #E8E4DF', background:'white', color:'#555567', fontSize:12, fontWeight:700, cursor: pagination.has_next ? 'pointer' : 'not-allowed', opacity: pagination.has_next ? 1 : .4 }}>
            Next ›
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', zIndex:9999,
          background: toast.type === 'error' ? '#C97B77' : '#2C2C35',
          color:'white', padding:'12px 24px', borderRadius:12, fontSize:13, fontWeight:700,
          boxShadow:'0 4px 20px rgba(0,0,0,.2)', whiteSpace:'nowrap', fontFamily:'Nunito,sans-serif' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}